import CloseIcon from '@mui/icons-material/Close';
import type { Theme } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import { makeStyles } from '@mui/styles';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { selectIsOneClickScribeModalOpen } from '../../selectors/ui';
import { setIsOneClickScribeModalOpen } from '../../store/slices/uiState';
import { useAppDispatch, useAppSelector } from '../../store/store';
import { OneClickScribeModalContent } from './OneClickScribeModalContent';

type Props = {
  hasScribe: boolean;
};

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: 440,
    padding: 32,
    borderRadius: 20,
    background: theme.palette.mode === 'light' ? '#FAF9F6' : theme.palette.v2.modalBackground.dark,
    boxShadow: '4px 2px 12px 0px rgba(112, 144, 176, 0.25)',
    [theme.breakpoints.down('sm')]: {
      width: 343,
      minWidth: 343,
    },
  },
  closeButton: {
    position: 'absolute',
    right: 8,
    top: 8,
    padding: 8,
    backgroundColor: 'rgba(0, 0, 0, 0.06)',
  },
  icon: {
    width: 20,
    height: 20,
    color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.32)' : 'rgba(0, 0, 0, 0.48)',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 16,
  },
  contentHeader: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    padding: '0px 48px 16px 48px',
  },
  contentHeaderIcon: {
    width: 32,
    height: 32,
  },
  contentHeaderText: {
    color: theme.palette.ava.green2,
    fontWeight: 700,
    fontSize: 32,
    textAlign: 'center',
    width: '90%',
    lineHeight: '98%',
  },
  contentBodyText: {
    fontSize: 24,
    fontWeight: 600,
    lineHeight: '98%',
    textAlign: 'center',
    color: theme.palette.mode === 'light' ? theme.palette.primary.main : theme.palette.ava.grey2,
  },
  contentBottom: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 32,
    marginTop: 12,
  },
  requestNowContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 1,
  },
  button: {
    width: 343,
    height: 48,
    padding: '14px 24px',
    borderRadius: 100,
    [theme.breakpoints.down('sm')]: {
      width: 279,
    },
  },
  requestNowButton: {
    background: 'linear-gradient(320deg, #006651 27.53%, #009778 76.48%)',
    color: 'white',
    fontSize: 18,
    fontWeight: 600,
    boxShadow: '4px 2px 12px 0px rgba(112, 144, 176, 0.25)',
    lineHeight: '113%',
    '&:hover': {
      boxShadow: 'inset 0 5px 15px rgba(0, 0, 0, 0.3)',
    },
  },
  description: {
    fontSize: 14,
    color: '#000000A3',
    lineHeight: 'normal',
  },
  laterButton: {
    gap: 8,
    border: '0.901px solid #006651',
    color: '#006651',
    fontSize: 18,
    fontWeight: 400,
    lineHeight: 'normal',
  },
  headsetContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: 48,
    height: 48,
    borderRadius: 30,
    padding: 12,
    gap: 10,
    color: 'white',
    backgroundColor: theme.palette.ava.green2,
  },
  hasScribeButton: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 8,
    borderRadius: 100,
    width: 343,
    padding: '12px 24px',
    [theme.breakpoints.down('sm')]: {
      width: 279,
    },
  },
  report: {
    padding: '12px 24px',
    border: '0.901px solid #006651',
  },
  reportText: {
    fontSize: 18,
    color: '#006651',
  },
  remove: {
    background: 'rgba(222, 66, 62, 0.12)',
    '&:hover': { background: 'rgba(222, 66, 62, 0.3)' },
  },
  removeText: {
    fontSize: 18,
    color: '#800000',
  },
  live: {
    display: 'flex',
    padding: '2px 10px',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 4,
    border: '1px solid rgba(0, 151, 120, 0.24)',
    borderRadius: 60,
    fontSize: 14,
    fontWeight: 600,
    color: theme.palette.ava.green2,
  },
  moreGapHeader: {
    gap: 8,
  },
  lessGapBottom: {
    gap: 16,
  },
  hr: {
    width: '100%',
    borderColor: '#00000014',
    height: 1,
  },
}));

function OneClickScribeModal({ hasScribe }: Readonly<Props>) {
  const { t } = useTranslation();
  const isModalOpen = useAppSelector(selectIsOneClickScribeModalOpen);

  const classes = useStyles();
  const dispatch = useAppDispatch();

  const handleClose = useCallback(() => dispatch(setIsOneClickScribeModalOpen(false)), []);

  return (
    <Dialog
      open={isModalOpen}
      onClose={handleClose}
      PaperProps={{ className: classes.container }}
      aria-labelledby="modal-one-click-scribe"
      aria-describedby="modal-request-scribe-quickly"
    >
      <IconButton aria-label="close" onClick={handleClose} className={classes.closeButton}>
        <CloseIcon className={classes.icon} />
      </IconButton>
      <OneClickScribeModalContent hasScribe={hasScribe} onClose={handleClose} />
    </Dialog>
  );
}

export default OneClickScribeModal;
